import { useQueryStringParam, useStringParam } from "../lib/ParamUtils";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  RecipeStoreDocument,
  RecipeStoreDocumentMetadata,
  StoredAiParserResult,
  StoredDiagnosticJson,
  StoredHtml,
  StoredParsedHtml,
  StoredVideoAnalysis,
  StoredVideoMeta,
  StoredVideoTranscript,
} from "@eatbetter/recipes-server/dist/internal/RecipeRawDataStore";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "../lib/AdminRedux";
import { getRecipeStoreDoc, listRecipeStoreDocs } from "../lib/recipes/AdminRecipesThunks";
import {
  displayUnexpectedErrorAndLog,
  FlexedSpinner,
  globalStyleConstants,
  Pressable,
  Spacer,
  TBody,
  THeading1,
  THeading2,
  TSecondary,
} from "@eatbetter/ui-shared";
import { RecipeId } from "@eatbetter/recipes-shared";
import { AdminScreenView } from "../components/AdminScreenView";
import { AdminRecipesScreenIdNav } from "./AdminRecipesScreen";
import { View } from "react-native";
import ReactJson from "react-json-view";
import { bottomThrow, daysBetween, defaultTimeProvider } from "@eatbetter/common-shared";
import { Divider } from "antd";
import { ImperfectMatch } from "@eatbetter/recipes-server/dist/internal/Hallucination";

const recipeIdParam = ":recipeId";
const docParam = "doc";
export const AdminRecipeDocumentsScreenNav = {
  getPath: (recipeId?: string, docName?: string) =>
    `/recipes/${recipeId ?? recipeIdParam}/documents${docName ? `?${docParam}=${encodeURIComponent(docName)}` : ""}`,
};

export const AdminRecipeDocumentsScreen = () => {
  const recipeId = useStringParam<RecipeId>(recipeIdParam).required();
  const [docs, setDocs] = useState<RecipeStoreDocumentMetadata[] | undefined>();
  const selectedName = useQueryStringParam(docParam).optional();
  const [loaded, setLoaded] = useState<{ name: string; doc: RecipeStoreDocument } | undefined>();
  const dispatch = useDispatch();
  const nav = useNavigate();

  useEffect(() => {
    dispatch(listRecipeStoreDocs(recipeId))
      .then(setDocs)
      .catch(err => {
        displayUnexpectedErrorAndLog("Error dispatching listRecipeStoreDocs", err);
      });
  }, [recipeId, dispatch, setDocs]);

  useEffect(() => {
    if (!selectedName) {
      return;
    }

    // a slow response for a doc that's no longer selected must not replace the selected one
    let current = true;
    dispatch(getRecipeStoreDoc(recipeId, selectedName))
      .then(doc => {
        if (current) {
          setLoaded({ name: selectedName, doc });
        }
      })
      .catch(err => {
        displayUnexpectedErrorAndLog("Error dispatching getRecipeStoreDoc", err);
      });

    return () => {
      current = false;
    };
  }, [recipeId, selectedName, dispatch]);

  const onDocSelected = (name: string) => {
    nav(AdminRecipeDocumentsScreenNav.getPath(recipeId, name));
  };

  const selectedMeta = useMemo(() => docs?.find(d => d.name === selectedName), [docs, selectedName]);
  const selectedDoc = loaded?.name === selectedName ? loaded?.doc : undefined;

  if (!docs) {
    return <FlexedSpinner />;
  }

  return (
    <AdminScreenView>
      <THeading1>
        Documents for <Link to={AdminRecipesScreenIdNav.getPath(recipeId)}>{recipeId}</Link>
      </THeading1>
      <Spacer vertical={2} />
      <View style={{ flexDirection: "row", flex: 1 }}>
        <View style={{ width: 250, borderRightWidth: 0.1, paddingRight: globalStyleConstants.unitSize }}>
          <DocList docs={docs} onClick={onDocSelected} selectedName={selectedName} />
        </View>
        <View style={{ paddingLeft: globalStyleConstants.unitSize, flex: 1 }}>
          {!selectedName && <TBody>No doc selected</TBody>}
          {!!selectedName && !selectedDoc && <FlexedSpinner />}
          {selectedDoc && (
            <>
              <THeading1>{selectedMeta ? getLabel(selectedMeta) : selectedName}</THeading1>
              <TBody>{new Date(selectedDoc.time).toLocaleString()}</TBody>
              <TBody>Git SHA: {selectedDoc.version}</TBody>
              <TBody>
                <a href={selectedDoc.adminS3SignedUrl} target="_blank" rel="noreferrer">
                  {selectedDoc.s3Url}
                </a>
              </TBody>
              <Spacer vertical={1} />
              {selectedDoc.type === "storedHtml" && <StoredHtmlComponent doc={selectedDoc} />}
              {selectedDoc.type === "parsedHtml" && <ParsedComponent doc={selectedDoc} />}
              {selectedDoc.type === "aiParserResult" && <AiParsedComponent doc={selectedDoc} />}
              {selectedDoc.type === "diagnosticJson" && <DiagnosticJsonComponent doc={selectedDoc} />}
              {selectedDoc.type === "storedVideoMeta" && <StoredVideoComponent doc={selectedDoc} />}
              {selectedDoc.type === "storedVideoTranscript" && <StoredVideoTranscriptComponent doc={selectedDoc} />}
              {selectedDoc.type === "storedVideoAnalysis" && <StoredVideoAnalysisComponent doc={selectedDoc} />}
            </>
          )}
        </View>
      </View>
    </AdminScreenView>
  );
};

const StoredHtmlComponent = (props: { doc: StoredHtml }) => {
  const shortened = props.doc.html.includes("Script removed because it had length of");
  return (
    <View>
      <TBody>
        Size: {props.doc.html.length} ({(props.doc.html.length / 1024 / 1024).toFixed(1)}mb)
      </TBody>
      {shortened && (
        <View style={{ backgroundColor: "yellow" }}>
          <TBody>HTML was shortened. Search for {"'Script removed because it had length of'"} to see where</TBody>
        </View>
      )}
      <TBody>Signature: {shortSig(props.doc.htmlSignature)}</TBody>
      <TBody>
        <a href={props.doc.url} target="_blank" rel="noreferrer">
          {props.doc.url}
        </a>
      </TBody>
      <Divider />
      <pre>{props.doc.html}</pre>
    </View>
  );
};

const ParsedComponent = (props: { doc: StoredParsedHtml }) => {
  return (
    <View>
      <TBody>Html Signature: {shortSig(props.doc.htmlSignature)}</TBody>
      <Divider />
      <ReactJson
        src={props.doc.parsed}
        displayDataTypes={false}
        displayObjectSize={false}
        quotesOnKeys={false}
        enableClipboard
        collapsed={false}
      />
    </View>
  );
};

const DiagnosticJsonComponent = (props: { doc: StoredDiagnosticJson }) => {
  return (
    <View>
      <ReactJson
        src={JSON.parse(props.doc.json)}
        displayDataTypes={false}
        displayObjectSize={false}
        quotesOnKeys={false}
        enableClipboard
        collapsed={false}
      />
    </View>
  );
};

const StoredVideoComponent = (props: { doc: StoredVideoMeta }) => {
  return (
    <View>
      <TBody>Length in Seconds: {props.doc.durationInSeconds}</TBody>
      <TBody>Size in MB: {(props.doc.sizeInBytes / 1000 / 1000).toFixed(1)}</TBody>
      <TBody>Downloaded via: {props.doc.downloadedVia}</TBody>
    </View>
  );
};

const StoredVideoTranscriptComponent = (props: { doc: StoredVideoTranscript }) => {
  return (
    <View>
      <TBody>Source: {JSON.stringify(props.doc.source)}</TBody>
      <TBody>Language: {props.doc.language}</TBody>
      <TBody>Type: {props.doc.format}</TBody>
      <pre>{props.doc.transcript}</pre>
    </View>
  );
};

const StoredVideoAnalysisComponent = (props: { doc: StoredVideoAnalysis }) => {
  return (
    <View>
      <pre>{JSON.stringify(props.doc.analysis, null, 2)}</pre>
    </View>
  );
};

const AiParsedComponent = (props: { doc: StoredAiParserResult }) => {
  const audit = props.doc.result.meta.audit;
  return (
    <View>
      <TBody>Html Signature: {shortSig(props.doc.htmlSignature ?? "")}</TBody>
      {audit && (
        <>
          <Divider />
          <TBody>Score: {audit.matchScore.toFixed(2)}</TBody>
          <TBody>Perfect matches: {audit.perfectMatchCount}</TBody>
          <TBody>Imperfect matches: {audit.imperfectMatches.length}</TBody>
          {audit.imperfectMatches.length > 0 && (
            <>
              <Spacer vertical={2} />
              <TSecondary>All strings normalized for comparison</TSecondary>
            </>
          )}
          {audit.imperfectMatches.map(im => (
            <ImperfectMatchComponent match={im} key={JSON.stringify(im)} />
          ))}
        </>
      )}
      <Divider />
      <ReactJson
        src={props.doc.result}
        displayDataTypes={false}
        displayObjectSize={false}
        quotesOnKeys={false}
        enableClipboard
        collapsed={false}
      />
      {!!props.doc.result.meta.input && (
        <>
          <Divider />
          <THeading2>Input</THeading2>
          <pre>{props.doc.result.meta.input}</pre>
        </>
      )}
      {!!props.doc.result.meta.audit?.normalizedInput && (
        <>
          <Divider />
          <THeading2>Normalized Input</THeading2>
          <View>
            <span style={{ fontFamily: "courier" }}>{props.doc.result.meta.audit?.normalizedInput}</span>
          </View>
        </>
      )}
    </View>
  );
};

const ImperfectMatchComponent = (props: { match: ImperfectMatch }) => {
  return (
    <table style={{ marginTop: globalStyleConstants.unitSize }}>
      <tbody style={{ fontFamily: "courier" }}>
        <tr>
          <td style={{ width: 100 }}>Query</td>
          <td>{props.match.normalizedQuery}</td>
        </tr>
        <tr>
          <td>Closest</td>
          <td>{props.match.normalizedClosestMatch}</td>
        </tr>
        <tr>
          <td>Score</td>
          <td>{props.match.score.toFixed(2)}</td>
        </tr>
      </tbody>
    </table>
  );
};

const DocList = (props: {
  docs: RecipeStoreDocumentMetadata[];
  onClick: (name: string) => void;
  selectedName?: string;
}) => {
  return (
    <View>
      {props.docs.map(doc => (
        <DocListItem doc={doc} onClick={props.onClick} selected={props.selectedName === doc.name} key={doc.name} />
      ))}
    </View>
  );
};

const DocListItem = (props: {
  doc: RecipeStoreDocumentMetadata;
  onClick: (name: string) => void;
  selected: boolean;
}) => {
  const { doc } = props;

  const onClick = useCallback(() => {
    props.onClick(doc.name);
  }, [props.onClick, doc.name]);

  const opacity = props.selected ? 1 : 0.6;
  const time = doc.metadata.time;

  return (
    <Pressable onPress={onClick}>
      <View style={{ marginBottom: globalStyleConstants.unitSize, opacity }}>
        <TBody fontWeight="medium">{getLabel(doc)}</TBody>
        <TSecondary>{new Date(time).toLocaleString()}</TSecondary>
        <TSecondary>{`${daysBetween(defaultTimeProvider(), time).toFixed(1)} days ago`}</TSecondary>
        {doc.metadata.type === "htmlMeta" && <TSecondary>Signature: {shortSig(doc.metadata.htmlSignature)}</TSecondary>}
        <TSecondary>Stored size: {formatBytes(doc.storedSizeInBytes)}</TSecondary>
      </View>
    </Pressable>
  );
};

function shortSig(sig: string): string {
  return sig.substring(0, 8);
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(0)}kb`;
  }
  return `${(bytes / 1024 / 1024).toFixed(1)}mb`;
}

function getLabel(doc: RecipeStoreDocumentMetadata): string {
  const meta = doc.metadata;
  switch (meta.type) {
    case "htmlMeta": {
      // html stored before source was added is server html
      const source = meta.source ?? "fetched";
      switch (source) {
        case "fetched":
          return "Server Html";
        case "admin":
          return "Admin Html";
        case "browserFetched":
          return "Browser Html";
        case "user":
          return "User Html";
        default:
          return bottomThrow(source);
      }
    }
    case "parsedMeta":
      return doc.docType === "aiParserResult" ? "AI Parsed" : "Parsed";
    case "diagnosticJson":
      return meta.title;
    case "storedVideo":
      return "Video";
    case "storedVideoTranscript":
      return `Video Transcript (${meta.language})`;
    case "storedVideoAnalysis":
      return "Video Analysis";
    default:
      return bottomThrow(meta);
  }
}
